import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Logo } from "@/components/AppShell";
import PricingTable from "@/components/PricingTable";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState(null);
  const [cycle, setCycle] = useState("monthly");
  const [currentPlan, setCurrentPlan] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadData = useCallback(() => {
    api.get("/config").then(({ data }) => setPlans(data.plans)).catch(() => {});
    if (user) api.get("/subscription").then(({ data }) => setCurrentPlan(data.effective_plan)).catch(() => {});
  }, [user]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const select = async (planId) => {
    if (!user) { navigate("/register"); return; }
    if (planId === "free") { navigate("/billing"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/checkout/subscription", {
        plan_id: planId, billing_cycle: cycle, origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <header className="glass-nav sticky top-0 z-50 border-b border-slate-200/60">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo />
          <Link to={user ? "/dashboard" : "/"} className="text-sm font-medium text-slate-600 flex items-center gap-1.5 hover:text-[#0A0F1C]" data-testid="pricing-back">
            <ArrowLeft size={16} /> {user ? "Dashboard" : "Home"}
          </Link>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-14">
        <div className="text-center max-w-2xl mx-auto">
          <h1 className="font-heading text-4xl font-extrabold tracking-tight text-[#0A0F1C]">Plans that scale with you</h1>
          <p className="mt-3 text-slate-600">Choose the plan that fits your growth stage. Cancel or change anytime.</p>
          <div className="mt-6 inline-flex items-center gap-1 bg-slate-100 rounded-full p-1">
            <button onClick={() => setCycle("monthly")} data-testid="pricing-cycle-monthly" className={`px-4 py-1.5 rounded-full text-sm font-medium ${cycle === "monthly" ? "bg-white shadow text-[#0A0F1C]" : "text-slate-500"}`}>Monthly</button>
            <button onClick={() => setCycle("yearly")} data-testid="pricing-cycle-yearly" className={`px-4 py-1.5 rounded-full text-sm font-medium ${cycle === "yearly" ? "bg-white shadow text-[#0A0F1C]" : "text-slate-500"}`}>Yearly · save ~17%</button>
          </div>
        </div>

        {busy && (
          <div className="mt-6 flex items-center justify-center gap-2 text-slate-500 text-sm">
            <Loader2 className="animate-spin" size={16} /> Redirecting to secure checkout…
          </div>
        )}

        <div className="mt-10">
          <PricingTable plans={plans} cycle={cycle} currentPlan={currentPlan} onSelect={select} ctaLabel={user ? "Upgrade" : "Get started"} />
        </div>

        <p className="mt-10 text-center text-xs text-slate-400">
          Secure payments by Stripe. Test mode card: 4242 4242 4242 4242.
        </p>
      </div>
    </div>
  );
}
